/**
 * Language Detection Utility
 * 
 * Automatically detects and applies the user's preferred language on first app launch.
 * This handles both web browsers and native apps (iOS/Android via Natively).
 */

import i18n from '@/i18n';
import { SUPPORTED_LANGUAGE_CODES } from '@/components/LanguageSelector';
import { applyLocaleUnitDefaults } from '@/lib/unitPreferences';

// Key to track if initial language detection has been performed
const LANGUAGE_DETECTED_KEY = 'gitfit_language_detected';

// Re-export for convenience
export const SUPPORTED_LANGUAGES = SUPPORTED_LANGUAGE_CODES;

/**
 * Get the device/browser's preferred language
 * @returns The detected language code or null if unable to detect
 */
export function getDeviceLanguage(): string | null {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return null;
  }

  try {
    // Get browser/device language preferences
    // navigator.languages returns an array of preferred languages in order of preference
    // navigator.language returns the primary language
    const languages = navigator.languages || [navigator.language];
    
    for (const lang of languages) {
      if (!lang) continue;
      
      // Normalize to BCP-47 format (e.g., 'en-GB', 'zh-Hans')
      const normalized = lang.replace('_', '-');
      
      // First check if the full locale code (e.g., 'en-GB') is supported
      if (SUPPORTED_LANGUAGES.includes(normalized)) {
        console.log('[LanguageDetection] Detected supported locale:', normalized, 'from', lang);
        return normalized;
      }
      
      // Fall back to the base language code (e.g., 'en-US' -> 'en')
      const baseCode = normalized.split('-')[0].toLowerCase();
      
      // Check if this language is supported
      if (SUPPORTED_LANGUAGES.includes(baseCode)) {
        console.log('[LanguageDetection] Detected supported language:', baseCode, 'from', lang);
        return baseCode;
      }
    }

    console.log('[LanguageDetection] No supported language found in:', languages);
    return null;
  } catch (error) {
    console.error('[LanguageDetection] Error detecting device language:', error);
    return null;
  }
}

/**
 * Check if this is the first time the app is being launched on this device
 * @returns true if this is the first launch
 */
export function isFirstLaunch(): boolean {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return false;
  }

  try {
    // Check if we've already performed language detection
    const detected = localStorage.getItem(LANGUAGE_DETECTED_KEY);
    return detected !== 'true';
  } catch (error) {
    console.error('[LanguageDetection] Error checking first launch:', error);
    return false;
  }
}

/**
 * Mark that initial language detection has been completed
 */
function markLanguageDetected(): void {
  if (typeof localStorage === 'undefined') return;
  
  try {
    localStorage.setItem(LANGUAGE_DETECTED_KEY, 'true');
  } catch (error) {
    console.error('[LanguageDetection] Error marking language detected:', error);
  }
}

/**
 * Detect and apply the device language on first launch
 * This should be called early in the app initialization
 * 
 * @returns The detected language code if applied, or null if not applicable
 */
export async function detectAndApplyLanguage(): Promise<string | null> {
  // Only run on first launch
  if (!isFirstLaunch()) {
    console.log('[LanguageDetection] Not first launch, skipping auto-detection');
    return null;
  }

  console.log('[LanguageDetection] First launch detected, attempting language detection...');

  // Get the device language
  const detectedLanguage = getDeviceLanguage();
  
  if (detectedLanguage) {
    try {
      // Check if i18n is ready
      if (i18n.isInitialized) {
        console.log('[LanguageDetection] Applying detected language:', detectedLanguage);
        await i18n.changeLanguage(detectedLanguage);
        console.log('[LanguageDetection] Language applied successfully:', detectedLanguage);
      } else {
        // Wait for i18n to initialize
        console.log('[LanguageDetection] Waiting for i18n initialization...');
        await new Promise<void>((resolve) => {
          i18n.on('initialized', () => {
            resolve();
          });
          // Timeout after 3 seconds
          setTimeout(resolve, 3000);
        });
        
        console.log('[LanguageDetection] Applying detected language after init:', detectedLanguage);
        await i18n.changeLanguage(detectedLanguage);
      }
      
      // Apply locale-based unit defaults on first launch
      applyLocaleUnitDefaults(detectedLanguage);
      
      // Mark detection as complete
      markLanguageDetected();
      return detectedLanguage;
    } catch (error) {
      console.error('[LanguageDetection] Error applying language:', error);
    }
  } else {
    console.log('[LanguageDetection] No supported language detected, using default');
  }

  // Mark detection as complete even if we didn't change the language
  markLanguageDetected();
  return null;
}

/**
 * Reset the language detection state (useful for testing or clearing app data)
 */
export function resetLanguageDetection(): void {
  if (typeof localStorage === 'undefined') return;
  
  try {
    localStorage.removeItem(LANGUAGE_DETECTED_KEY);
    console.log('[LanguageDetection] Language detection state reset');
  } catch (error) {
    console.error('[LanguageDetection] Error resetting language detection:', error);
  }
}
